import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Phone, Loader2, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { business } from "@/data/content";
import { trackInquiryConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const waterSources = ["Rainwater tank", "Bore water", "Town water", "Unsure"];
const serviceOptions = ["Tank cleaning", "Filtration", "UV system", "Water testing", "BioCleanse", "Bore water treatment", "System servicing", "Other"];

const initial = {
  name: "", phone: "", email: "", suburb: "",
  water_source: "", service_required: "",
  description: "", preferred_contact: "Phone", consent: false, website: "",
};

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (form.phone.trim().length < 6) e.phone = "Please enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (form.suburb.trim().length < 2) e.suburb = "Please enter your suburb.";
    if (!form.water_source) e.water_source = "Please select your water source.";
    if (!form.service_required) e.service_required = "Please select a service.";
    if (!form.consent) e.consent = "Please provide consent to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      trackInquiryConversion();
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors({ form: typeof detail === "string" ? detail : "Something went wrong. Please call us instead." });
    } finally {
      setSubmitting(false);
    }
  };

  const errClass = (k) => (errors[k] ? "border-destructive focus-visible:ring-destructive" : "border-pristine-aqua");

  return (
    <section id="contact" className="py-14 lg:py-20 aqua-gradient" data-testid="enquiry-section">
      <div className="mx-auto max-w-3xl px-5">
        <Reveal className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-pristine-aquadeep mb-4">Request a Quote</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-pristine-navy tracking-tight text-balance">
            Tell Us About Your Water
          </h2>
          <p className="mt-4 text-base text-pristine-navy/70">
            Complete the form below and our local team will be in touch.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-[2rem] bg-white border border-pristine-aqua shadow-[0_20px_60px_rgba(15,58,112,0.10)] p-6 sm:p-10">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
                data-testid="enquiry-success"
              >
                <CheckCircle2 className="h-16 w-16 text-pristine-aquadeep mx-auto" />
                <h3 className="mt-5 font-heading text-2xl font-medium text-pristine-navy">Thank you — enquiry received</h3>
                <p className="mt-3 text-pristine-navy/70 max-w-md mx-auto">
                  Our local Sunshine Coast team will review your enquiry and be in touch soon. For urgent
                  enquiries, please call us directly.
                </p>
                <a href={business.phoneHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-pristine-blue text-white px-7 h-13 py-3.5 font-semibold" data-testid="success-call-cta">
                  <Phone className="h-5 w-5" /> Call {business.phone}
                </a>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="enquiry-form" noValidate>
                {/* honeypot */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-pristine-navy font-medium">Name</Label>
                    <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("name")}`} />
                    {errors.name && <p className="mt-1.5 text-xs text-destructive" data-testid="error-name">{errors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-pristine-navy font-medium">Phone</Label>
                    <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Your contact number" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("phone")}`} />
                    {errors.phone && <p className="mt-1.5 text-xs text-destructive" data-testid="error-phone">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label className="text-pristine-navy font-medium">Email</Label>
                    <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("email")}`} />
                    {errors.email && <p className="mt-1.5 text-xs text-destructive" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-pristine-navy font-medium">Property Suburb</Label>
                    <Input data-testid="input-suburb" value={form.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="e.g. Noosaville" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("suburb")}`} />
                    {errors.suburb && <p className="mt-1.5 text-xs text-destructive" data-testid="error-suburb">{errors.suburb}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-pristine-navy font-medium">Water Source</Label>
                    <Select value={form.water_source} onValueChange={(v) => set("water_source", v)}>
                      <SelectTrigger data-testid="select-water-source" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("water_source")}`}>
                        <SelectValue placeholder="Select water source" />
                      </SelectTrigger>
                      <SelectContent>
                        {waterSources.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.water_source && <p className="mt-1.5 text-xs text-destructive" data-testid="error-water-source">{errors.water_source}</p>}
                  </div>
                  <div>
                    <Label className="text-pristine-navy font-medium">Service Required</Label>
                    <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
                      <SelectTrigger data-testid="select-service" className={`mt-2 h-12 rounded-xl bg-pristine-surface ${errClass("service_required")}`}>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.service_required && <p className="mt-1.5 text-xs text-destructive" data-testid="error-service">{errors.service_required}</p>}
                  </div>
                </div>

                <div>
                  <Label className="text-pristine-navy font-medium">Brief Description</Label>
                  <Textarea data-testid="input-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell us a little about your water system or concern (optional)" className="mt-2 min-h-[110px] rounded-xl bg-pristine-surface border-pristine-aqua" />
                </div>

                <div>
                  <Label className="text-pristine-navy font-medium">Preferred Contact Method</Label>
                  <RadioGroup value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)} className="mt-3 flex gap-6" data-testid="radio-contact">
                    {["Phone", "Email"].map((m) => (
                      <div key={m} className="flex items-center gap-2">
                        <RadioGroupItem value={m} id={`contact-${m}`} data-testid={`radio-contact-${m.toLowerCase()}`} />
                        <Label htmlFor={`contact-${m}`} className="text-pristine-navy/80 cursor-pointer">{m}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <div className="flex items-start gap-3">
                    <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="checkbox-consent" className="mt-1 border-pristine-aquadeep data-[state=checked]:bg-pristine-blue" />
                    <Label htmlFor="consent" className="text-sm text-pristine-navy/75 leading-relaxed cursor-pointer font-normal">
                      I consent to Pristine Water Systems contacting me about my enquiry.
                    </Label>
                  </div>
                  {errors.consent && <p className="mt-1.5 text-xs text-destructive" data-testid="error-consent">{errors.consent}</p>}
                </div>

                {errors.form && <p className="text-sm text-destructive text-center" data-testid="error-form">{errors.form}</p>}

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-enquiry"
                  className="w-full rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white h-14 text-base font-semibold"
                >
                  {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <><Send className="mr-2 h-5 w-5" /> Request My Quote</>}
                </Button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
